import { computed, effect } from '@angular/core';
import {
  getState,
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { UserApplications } from '@workspace/data-types';
import { LocalStorageKey } from '../../models';
import { withStoreDevtools } from '../with-devtools';

export enum ApplicationLayouts {
  EMPTY = 'empty',
  CLASSIC = 'classic',
  CLASSY = 'classy',
  COMPACT = 'compact',
  DENSE = 'dense',
  FUTURISTIC = 'futuristic',
  THIN = 'thin',
  CENTERED = 'centered',
  ENTERPRISE = 'enterprise',
  MATERIAL = 'material',
  MODERN = 'modern',
}

export interface ApplicationState {
  name: UserApplications | null;
  version: string | null;
  layout: ApplicationLayouts | null;
  debug: boolean;
}

const initialState: ApplicationState = {
  name: null,
  version: null,
  layout: null,
  debug: typeof localStorage !== 'undefined' && localStorage.getItem(LocalStorageKey.DEBUG_MODE) === 'true',
};

export const ApplicationStore = signalStore(
  { providedIn: 'root' },
  withStoreDevtools('application'),
  withState(initialState),

  withMethods((store) => ({
    loadApplication(config: { name: UserApplications; layout: ApplicationLayouts }) {
      patchState(store, {
        name: config.name,
        layout: config.layout,
        version: '1',
      });
    },

    updateLayout(layout: ApplicationLayouts) {
      patchState(store, { layout });
    },

    toggleDebug(debugMode: boolean) {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(LocalStorageKey.DEBUG_MODE, debugMode ? 'true' : 'false');
      }
      patchState(store, { debug: debugMode });
    },
  })),

  withComputed((store) => ({
    debugMode: computed(() => store.debug()),
    currentApplicationName: computed(() => store.name()),
    currentLayout: computed(() => store.layout()),
  })),

  withHooks((store) => ({
    onInit() {
      effect(() => {
        if (store.debug()) {
          console.debug('Application state:', getState(store));
        }
      });
    },
  })),
);
